/* eslint-disable @typescript-eslint/naming-convention */
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import React, { FC, ReactNode } from 'react';
import { TypographyProps } from 'src/components-v2/DataDisplay/Typography/Typography.types';
import { RichTextSwitch } from 'src/containers';
import { CmsMediaSwitch } from 'src/components-v2/Media/CmsMediaSwitch';
import MainNavigationSpacer from 'src/components-v2/MainNavigationBar/MainNavigationSpacer';
import {
  Column,
  Section,
  Container,
  Row,
  Stack,
  Box,
} from 'src/components-v2/Layout';
import type { RichTextContent, Sys } from 'src/types/contentful-sdk-compat';
import { CmsAssetProps } from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch.types';
import { BackgroundContext } from 'src/contexts';
import type { HeroElement } from 'src/contexts/FirstHeroContext/FirstHeroContext';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { CmsThemes, ComponentModes } from 'src/lib/constants';
import ThemeProvider from 'threads5/themes/theme-provider';
import type { ITheme } from 'threads5/themes/theme';

export interface FormWrapperProps {
  children: ReactNode;
  backgroundColor?: string;
  textColor?: string;
  mode?: string;
  body?: RichTextContent | null;
  formHeadline?: RichTextContent | null;
  formSys?: Sys;
  backgroundAsset?: CmsAssetProps | null;
  sys?: Sys;
  id: string;
  component?: HeroElement;
}

// the persistent holographic card border approximates Figma's
// "holo-gradient-light" image stroke with the conic-gradient recipe already
// used by the cards grid (src/sections/Cards/CardsGrid/Card.tsx)
const holoBorder = (theme: ITheme) => {
  return `linear-gradient(white, white) padding-box, conic-gradient(from 180deg, ${theme.palette['base/pink/300']} 0%, ${theme.palette['base/red/300']} 15%, ${theme.palette['base/yellow/300']} 30%, ${theme.palette['base/lime/300']} 46%, ${theme.palette['base/sky/300']} 64%, ${theme.palette['base/purple/300']} 81%, ${theme.palette['base/pink/300']} 100%) border-box`;
};

// the card stays white in dark mode, so the headline keeps fixed light
// colors instead of the mode-flipping web3/text tokens
const formHeadlineSx = {
  textAlign: 'center',
  color: 'base/sky/1000',
};

const formHeadlineNodes: { [key: string]: Partial<TypographyProps> } = {
  h1: {
    component: 'h1',
    variant: 'h4',
    fieldId: 'richDescription',
    sx: formHeadlineSx,
  },
  h2: {
    component: 'h2',
    variant: 'h4',
    fieldId: 'richDescription',
    sx: formHeadlineSx,
  },
  h3: {
    component: 'h3',
    variant: 'h4',
    fieldId: 'richDescription',
    sx: formHeadlineSx,
  },
  p: {
    component: 'p',
    variant: 'p',
    fieldId: 'richDescription',
    sx: formHeadlineSx,
  },
};

export const FormWrapper: FC<FormWrapperProps> = ({
  children,
  sys,
  backgroundColor,
  textColor = 'black',
  mode: cmsMode,
  body,
  formHeadline,
  formSys,
  backgroundAsset,
  id,
  component = 'section',
}) => {
  const cmsTheme = useCmsTheme();
  const isWeb3 = cmsTheme === CmsThemes.WEB3;
  const isTextColorWhite = textColor === 'white';
  const resolvedMode = useCmsMode({ mode: cmsMode, backgroundColor });
  // legacy entries express darkness only via textColor='white' (mode was
  // backfilled to 'light', and useCmsMode gives solid background colors
  // precedence over mode); white always meant white copy on web 2.0, so any
  // surface this breaks was already broken there. Deletable once those
  // entries move to mode='dark' AND backgroundColor='transparent' (SITE-5985)
  const mode = isTextColorWhite
    ? ComponentModes.DARK
    : resolvedMode || ComponentModes.LIGHT;
  const isDark = mode === ComponentModes.DARK;
  const componentTheme = useComponentTheme(mode);
  const cardTheme = useComponentTheme(ComponentModes.LIGHT);
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  const textColorStyle = {
    color: textColor,
  };

  const contentNodes: { [key: string]: Partial<TypographyProps> } = {
    h1: {
      component: 'h1',
      variant: 'h1',
      fieldId: 'leftColumn',
      sx: isWeb3 ? { color: 'web3/text/heading' } : { ...textColorStyle },
    },
    h2: {
      component: 'h2',
      variant: 'h2',
      fieldId: 'leftColumn',
      sx: isWeb3 ? { color: 'web3/text/heading' } : { ...textColorStyle },
    },
    h3: {
      component: 'h3',
      variant: 'h3',
      fieldId: 'leftColumn',
      sx: isWeb3 ? { color: 'web3/text/heading' } : { ...textColorStyle },
    },
    h6: {
      component: 'h6',
      variant: 'h6',
      className: 'eyebrow-heading',
      fieldId: 'leftColumn',
      sx: isWeb3
        ? { color: 'web3/text/eyebrow' }
        : { color: isTextColorWhite ? 'white' : 'var(--black800)' },
    },
    p: {
      component: 'p',
      variant: 'p',
      fieldId: 'leftColumn',
      sx: isWeb3
        ? { color: 'web3/text/paragraph' }
        : { ...textColorStyle, fontSize: { lg: '2.4rem' } },
    },
  };

  const formNodes: { [key: string]: Partial<TypographyProps> } = {
    h1: {
      component: 'h1',
      variant: 'h1',
      fieldId: 'leftColumn',
    },
    h2: {
      component: 'h2',
      variant: 'h2',
      fieldId: 'leftColumn',
    },
    h3: {
      component: 'h3',
      variant: 'h3',
      fieldId: 'leftColumn',
    },
    h6: {
      component: 'h6',
      variant: 'h6',
      className: 'eyebrow-heading',
      fieldId: 'leftColumn',
      sx: {
        color: 'var(--black800)',
      },
    },
    p: {
      component: 'p',
      variant: 'p',
      fieldId: 'leftColumn',
      sx: {
        fontSize: { lg: '2.4rem' },
      },
    },
  };

  if (isWeb3) {
    const background =
      componentTheme.palette[backgroundColor] || backgroundColor;

    return (
      <ThemeProvider theme={componentTheme}>
        <Section
          component={component}
          id={id}
          // dark mode's visual surface comes from the asset or an ancestor,
          // not the backgroundColor field, so publish a dark
          // BackgroundContext for descendants that resolve their own
          // treatment from it (list markers, legacy buttons)
          backgroundColor={isDark ? 'black' : backgroundColor}
          sx={{
            position: 'relative',
            background,
          }}
          {...inspectorProps({ fieldId: 'internalTitle' })}
        >
          {backgroundAsset && (
            <CmsMediaSwitch
              asset={backgroundAsset}
              queryString={formImageQueryString}
              sx={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}

          {/* the nav floats over the section, so its height is part of the
              Figma content offsets (128/132/152) — the spacer owns the nav
              height and pt keeps only the design's own gap */}
          <MainNavigationSpacer />
          <Container
            sx={{
              pt: { xs: '48px', lg: '56px' },
              pb: { xs: '48px', lg: '80px' },
              zIndex: 1,
              position: 'relative',
            }}
          >
            <Row sx={{ m: '0', alignItems: 'flex-start' }}>
              {/* desktop columns sit inside the site's 2..22 content band
                  (the same one the web 2.0 branch and the nav use); Figma's
                  1372px content frame maps to that band, not the full
                  container */}
              <Column xs={24} xsOffset={0} lg={9} lgOffset={2} sx={{ p: '0' }}>
                <Box
                  sx={{
                    // values from the Web Design System Figma, Hero Form
                    // Asset Background
                    pt: { xs: '0', lg: '131px' },
                    mb: { xs: '32px', sm: '40px', lg: '0' },
                    // do not add .decorative-text here: those spans sit
                    // inside headlines on the gated pages and this treatment
                    // would shrink them to 14px (spacing for decorative text
                    // is owned by the :has() rule below)
                    '& .eyebrow-heading': {
                      mb: '1.6rem',
                      fontSize: '1.4rem',
                      fontWeight: 700,
                      letterSpacing: '0.125rem',
                    },
                    '& h1': {
                      mb: { xs: '1.6rem', lg: '2.4rem' },
                      fontSize: { xs: '4.0rem', lg: '6.0rem' },
                      lineHeight: { xs: '114%', lg: '108%' },
                      letterSpacing: '-0.2rem',
                      maxWidth: { lg: '609px' },
                    },
                    '& h2, & h3': {
                      mb: { xs: '1.6rem', lg: '2.4rem' },
                    },
                    '& p': {
                      mb: '1.6rem',
                      fontSize: '2rem',
                      lineHeight: { xs: 1.4, lg: 1.5 },
                      maxWidth: { lg: '609px' },
                    },
                    '& p:nth-last-of-type(1):empty': {
                      display: 'none',
                    },
                    '& .button-group': {
                      flexDirection: { xs: 'column', sm: 'row' },
                      flexWrap: 'wrap',
                      gap: { xs: '1.8rem', sm: '1.2rem', lg: '2.6rem' },
                    },
                    // for decorative text
                    '& p:has(.decorative-text), h1:has(.decorative-text)': {
                      mb: { xs: 1, md: 2 },
                      // for icons
                      '& svg': {
                        mt: { xs: '0.2rem', md: '0.4rem' },
                        zoom: { xs: 1, md: 1.2 }, // we use zoom because it works for all icons
                      },
                    },
                    // legacy entries (textColor='white') color decorative
                    // headline text per breakpoint for the old layout, where
                    // the mobile copy sat inside the white card — on the
                    // now-uniform dark surface those authored colors go
                    // dark-on-dark, so inherit instead (gradients paint
                    // DecorativeText's inner span, so unaffected). Dies with
                    // the same SITE-5985 content migration as the mode shim
                    ...(isTextColorWhite && {
                      '& .decorative-text': {
                        color: 'inherit',
                      },
                    }),
                  }}
                >
                  <RichTextSwitch
                    sys={sys}
                    content={body}
                    nodes={contentNodes}
                  />
                </Box>
              </Column>

              <Column xs={24} xsOffset={0} lg={10} lgOffset={1} sx={{ p: '0' }}>
                {/* the card stays white in dark mode, so its subtree is
                    pinned to the light theme (same approach as the cards
                    grid Card) */}
                <ThemeProvider theme={cardTheme}>
                  <BackgroundContext.Provider value='white'>
                    <Stack
                      sx={{
                        border: '3px solid transparent',
                        borderRadius: { xs: '24px', lg: '30px' },
                        background: holoBorder,
                        // content insets from Figma (measured from the card
                        // edge, so minus the 3px border)
                        p: { xs: '37px 21px', lg: '35px 45px 45px' },
                        '& input': {
                          borderRadius: '8px',
                        },
                        '& i + span': {
                          fontSize: '1.4rem',
                        },
                        '& fieldset': {
                          '& div:has(input)': {
                            borderRadius: '8px',
                          },
                        },
                        '& p:nth-last-of-type(1):empty': {
                          display: 'none',
                        },
                      }}
                    >
                      {formHeadline && (
                        <Box
                          sx={{
                            mb: { xs: '32px', lg: '33px' },
                            '& h1, & h2, & h3, & p': {
                              mb: '0',
                            },
                          }}
                        >
                          <RichTextSwitch
                            sys={formSys || sys}
                            content={formHeadline}
                            nodes={formHeadlineNodes}
                          />
                        </Box>
                      )}

                      {children}
                    </Stack>
                  </BackgroundContext.Provider>
                </ThemeProvider>
              </Column>
            </Row>
          </Container>
        </Section>
      </ThemeProvider>
    );
  }

  return (
    <Section
      component={component}
      id={id}
      sx={{
        position: 'relative',
        backgroundColor,
      }}
      {...inspectorProps({ fieldId: 'internalTitle' })}
    >
      {backgroundAsset && (
        <CmsMediaSwitch
          asset={backgroundAsset}
          queryString={formImageQueryString}
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      <Container
        sx={{
          py: { xs: '16px', sm: '50px', lg: '100px' },
          zIndex: 1,
          position: 'relative',
        }}
      >
        <Row sx={{ m: '0', alignItems: 'flex-start' }}>
          <Column
            xs={24}
            xsOffset={0}
            sm={20}
            smOffset={2}
            lg={8}
            lgOffset={2}
            sx={{
              display: { xs: 'none', lg: 'block' },
              p: '0',
            }}
          >
            <Box
              sx={{
                pt: { xs: '0', lg: '8.9rem' },
                '& .eyebrow-heading, .decorative-text': {
                  mb: { xs: '1.6rem', lg: '2.4rem' },
                },
                '& h1': {
                  mb: { xs: '1.6rem', lg: '2.4rem' },
                  maxWidth: { lg: '563px' },
                },
                '& h2': {
                  mb: { xs: '1.6rem', lg: '2.4rem' },
                },
                '& h3': {
                  mb: { xs: '1.6rem', lg: '2.4rem' },
                },
                '& p': {
                  mb: { xs: '1.6rem', lg: '2.4rem' },
                  maxWidth: { lg: '476px' },
                },
                '& p:nth-last-of-type(1):empty': {
                  display: 'none',
                },
                '& .button-group': {
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  gap: { xs: '1.8rem', sm: '1.2rem', lg: '2.6rem' },
                },
                // for decorative text
                '& p:has(.decorative-text), h1:has(.decorative-text)': {
                  mb: { xs: 1, md: 2 },
                  // for icons
                  '& svg': {
                    mt: { xs: '0.2rem', md: '0.4rem' },
                    zoom: { xs: 1, md: 1.2 }, // we use zoom because it works for all icons
                  },
                },
              }}
            >
              <RichTextSwitch sys={sys} content={body} nodes={contentNodes} />
            </Box>
          </Column>

          <Column
            xs={24}
            xsOffset={0}
            sm={20}
            smOffset={2}
            lg={10}
            lgOffset={2}
            sx={{
              backgroundColor: 'white',
              borderRadius: '12px',
              p: '0',
            }}
          >
            <Row
              container
              columns={{ xs: 22, sm: 20, lg: 10 }}
              spacing={{ xs: '0.4rem', sm: '0.8rem', md: '1.6rem' }}
              sx={{
                height: '100%',
              }}
            >
              <Column
                xsOffset={2}
                xs={18}
                smOffset={1}
                sm={18}
                lgOffset={1}
                lg={8}
                sx={{
                  p: '0',
                }}
              >
                <Stack
                  sx={{
                    py: {
                      xs: '3.4rem',
                      sm: '4.0rem',
                      lg: '6.4rem',
                    },
                    '& input': {
                      borderRadius: '8px',
                    },
                    '& i + span': {
                      fontSize: '1.4rem',
                    },
                    '& fieldset': {
                      '& div:has(input)': {
                        borderRadius: '8px',
                      },
                    },
                    '& .eyebrow-heading, .decorative-text': {
                      mb: { xs: '1.6rem', lg: '2.4rem' },
                    },
                    '& h1': {
                      mb: { xs: '1.6rem', lg: '2.4rem' },
                    },
                    '& h2': {
                      mb: { xs: '1.6rem', lg: '2.4rem' },
                    },
                    '& h3': {
                      mb: { xs: '1.6rem', lg: '2.4rem' },
                    },
                    '& p': {
                      mb: { xs: '1.6rem', lg: '2.4rem' },
                    },
                    '& p:nth-last-of-type(1):empty': {
                      display: 'none',
                    },
                    '& .button-group': {
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: '1.8rem', sm: '1.2rem', lg: '2.6rem' },
                    },
                    // for decorative text
                    '& p:has(.decorative-text), h1:has(.decorative-text)': {
                      mb: { xs: 1, md: 2 },
                      // for icons
                      '& svg': {
                        mt: { xs: '0.2rem', md: '0.4rem' },
                        zoom: { xs: 1, md: 1.2 }, // we use zoom because it works for all icons
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: { lg: 'none' },
                      mb: '1.6rem',
                      maxWidth: { sm: '533px', lg: 'none' },
                    }}
                  >
                    <RichTextSwitch
                      sys={sys}
                      content={body}
                      nodes={formNodes}
                    />
                  </Box>

                  {children}
                </Stack>
              </Column>
            </Row>
          </Column>
        </Row>
      </Container>
    </Section>
  );
};

const BASE = '?fm=webp&q=70';
const formImageQueryString = {
  xs: `${BASE}&w=800`,
  sm: `${BASE}&w=1234`,
  md: `${BASE}&w=1902`,
  lg: `${BASE}&w=2430`,
};
